import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicNavbar from '../components/PublicNavbar';
import Footer from '../components/Footer';
import {
  Package,
  TrendingUp,
  ShieldCheck,
  Settings,
  Truck,
  Activity,
  ArrowRight,
  CheckCircle2,
  PhoneCall,
  BarChart4,
  Wallet,
  Smartphone,
  Globe2,
  ChevronRight,
  Star
} from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();


  const solutions = [
    {
      title: 'For D2C Brands',
      subtitle: 'Scale your private label faster',
      icon: TrendingUp,
      features: ['Smart Courier Allocation', 'Branded Tracking Page', 'API Integrations', 'Abandoned Cart Recovery'],
      color: 'bg-indigo-50 text-indigo-700',
      iconBg: 'bg-indigo-100',
    },
    {
      title: 'For Social Sellers',
      subtitle: 'Make your hustle easier',
      icon: Smartphone,
      features: ['COD Order Confirmation', 'WhatsApp Buyer Alerts', 'Multi-Courier Shipping', 'NDR Follow Ups'],
      color: 'bg-emerald-50 text-emerald-700',
      iconBg: 'bg-emerald-100',
    },
    {
      title: 'For Enterprise',
      subtitle: 'Flexible, reliable mass scale',
      icon: Globe2,
      features: ['Custom Workflow Integrations', 'Advanced Courier Rules', 'Postpaid Billing', 'Dedicated Account Manager'],
      color: 'bg-slate-50 text-slate-700',
      iconBg: 'bg-slate-200',
    }
  ];

  const features = [
    {
      title: 'Branded Tracking Experience',
      desc: 'Build trust with a custom tracking page featuring your logo, header, and promotional banners — at no extra cost.',
      icon: Activity
    },
    {
      title: 'Automated NDR Management',
      desc: 'Reduce RTOs by proactively engaging buyers through automated IVR calls and SMS when a delivery fails.',
      icon: Settings
    },
    {
      title: 'Early COD Remittance',
      desc: 'Keep your cash flow healthy. Get your Cash on Delivery payments remitted to your account faster than industry standards.',
      icon: Wallet
    },
    {
      title: 'Real-Time Data Analytics',
      desc: 'Track operational bottlenecks, monitor courier performance, and get actionable insights to scale your business.',
      icon: BarChart4
    }
  ];

  const testimonials = [
    {
      name: "Arjun Sharma",
      company: "Urban Threads",
      metric: "200% Growth Yearly",
      quote: "The best part of partnering with Flywell is the proactive NDR management. Our RTOs dropped by 30% in the first quarter alone."
    },
    {
      name: "Priyanka Desai",
      company: "Glow & Co.",
      metric: "99.8% Delivery Success",
      quote: "Flywell's Smart Courier Allocation completely automated our logistics. It feels like we have a dedicated logistics team in-house."
    },
    {
      name: "Rahul Verma",
      company: "TechGear India",
      metric: "2x Faster COD",
      quote: "Early COD remittance changed the game for us. Our cash flow is steady, allowing us to invest heavily in inventory & marketing."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 font-sans selection:bg-indigo-500 selection:text-white">
      <PublicNavbar />


      {/* Hero Section */}
      <section className="relative pt-32 lg:pt-48 pb-20 overflow-hidden">
        {/* Background Gradients */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] opacity-30 bg-gradient-to-r from-indigo-400 via-purple-400 to-emerald-300 blur-[100px] -z-10 rounded-full pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-sm font-semibold mb-6">
                <span className="w-2 h-2 rounded-full bg-indigo-600 animate-pulse"></span>
                Powering Next-Gen eCommerce
              </div>
              <h1 className="text-5xl lg:text-7xl font-black text-slate-900 leading-[1.1] mb-6 tracking-tight">
                Logistics Built <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">For The Bold.</span>
              </h1>
              <p className="text-lg lg:text-xl text-slate-600 mb-8 leading-relaxed">
                Fuel your ambition with an intelligent logistics platform designed for scale. Take your brand to 29,000+ pincodes effortlessly without breaking your bank or backend.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate('/signup')}
                  className="px-8 py-4 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all font-semibold text-lg flex items-center justify-center gap-2 shadow-lg shadow-indigo-200 group"
                >
                  Create Free Account
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <button
                  onClick={() => navigate('/contact')}
                  className="px-8 py-4 bg-white text-slate-700 rounded-xl hover:border-slate-300 transition-all border border-slate-200 font-semibold text-lg shadow-sm"
                >
                  Talk to an Expert
                </button>
              </div>
            </div>

            {/* Hero Visual */}
            <div className="relative mx-auto w-full max-w-lg lg:max-w-none">
              <div className="relative rounded-2xl bg-white shadow-2xl border border-slate-100 p-6 z-20">
                <div className="flex justify-between items-center mb-6 border-b border-slate-100 pb-4">
                  <div className="space-y-1">
                    <div className="text-sm font-semibold text-slate-500">Total Shipments</div>
                    <div className="text-3xl font-black text-slate-900">12,459</div>
                  </div>
                  <div className="bg-emerald-50 text-emerald-600 px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                    <TrendingUp className="w-4 h-4" /> +24%
                  </div>
                </div>
                <div className="space-y-3">
                  {[
                    { id: '#ORD-3847', courier: 'Delhivery Surface', status: 'In Transit', time: '5 mins ago', iconBg: 'bg-blue-100', iconColor: 'text-blue-600', statusColor: 'text-blue-600' },
                    { id: '#ORD-3848', courier: 'XpressBees', status: 'Out for Delivery', time: '12 mins ago', iconBg: 'bg-amber-100', iconColor: 'text-amber-600', statusColor: 'text-amber-600' },
                    { id: '#ORD-3849', courier: 'Shadowfax', status: 'Delivered', time: '2 mins ago', iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600', statusColor: 'text-emerald-600' },
                  ].map((shipment, i) => (
                    <div key={i} className="flex items-center gap-3 bg-slate-50 p-3 rounded-lg border border-slate-100 animate-pulse hover:animate-none transition-all">
                      <div className={`w-10 h-10 ${shipment.iconBg} rounded-lg flex items-center justify-center shrink-0`}>
                        <Package className={`w-5 h-5 ${shipment.iconColor}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-slate-800 truncate">{shipment.id}</div>
                        <div className="text-xs text-slate-400 truncate">{shipment.courier}</div>
                      </div>
                      <div className="flex flex-col items-end shrink-0">
                        <div className={`text-xs font-bold ${shipment.statusColor}`}>{shipment.status}</div>
                        <div className="text-xs text-slate-400">{shipment.time}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Floating Decorative Elements */}
              <div className="absolute -top-6 -right-6 bg-slate-900 text-white p-4 rounded-xl shadow-xl z-30 animate-bounce" style={{ animationDuration: '3s' }}>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                  <div>
                    <div className="text-sm font-bold">Courier Assigned</div>
                    <div className="text-xs text-slate-300">Delhivery Surface</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Integration Marquee / Partners */}
      <section className="py-10 border-y border-slate-200 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-6">Managing 25+ eCommerce Platforms & Couriers</p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-60 grayscale hover:grayscale-0 transition-all duration-500">
            {['Shopify', 'WooCommerce', 'Magento', 'Delhivery', 'NimbusPost', 'SKart', 'XpressBees'].map((partner, i) => (
              <div key={i} className="text-xl md:text-2xl font-black text-slate-800 tracking-tighter">
                {partner}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Audience Solutions */}
      <section id="solutions" className="py-24 bg-slate-50 scroll-mt-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-5xl font-black text-slate-900 mb-4 tracking-tight">Turning Capabilities into Possibilities</h2>
            <p className="text-lg text-slate-600">Built to handle complexity, designed for absolute simplicity. Select your profile to see how we help.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {solutions.map((sol, idx) => {
              const Icon = sol.icon;
              return (
                <div key={idx} className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm hover:shadow-xl transition-shadow duration-300 relative overflow-hidden group">
                  <div className={`absolute top-0 right-0 w-32 h-32 opacity-10 rounded-bl-full ${sol.color.split(' ')[0]} -mr-4 -mt-4 transition-transform group-hover:scale-110`}></div>
                  <div className={`w-14 h-14 ${sol.iconBg} ${sol.color.split(' ')[1]} rounded-xl flex items-center justify-center mb-6`}>
                    <Icon className="w-7 h-7" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">{sol.title}</h3>
                  <p className="text-slate-500 font-medium mb-8">{sol.subtitle}</p>

                  <ul className="space-y-4 mb-8">
                    {sol.features.map((feat, fidx) => (
                      <li key={fidx} className="flex items-start gap-3">
                        <CheckCircle2 className={`w-5 h-5 ${sol.color.split(' ')[1]} mt-0.5 shrink-0`} />
                        <span className="text-slate-700 font-medium">{feat}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => navigate('/signup')}
                    className={`font-bold flex items-center gap-1 hover:gap-2 transition-all ${sol.color.split(' ')[1]}`}
                  >
                    Explore Solutions <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Global Reach Highlight: Domestic vs International */}
      <section className="py-24 bg-white border-y border-slate-200 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-5xl font-black text-slate-900 mb-4 tracking-tight">No Boundaries, Just Growth</h2>
            <p className="text-lg text-slate-600">Conquer local streets or cross international borders with exactly the same ease.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Domestic Block */}
            <div className="bg-indigo-50 border border-indigo-100 rounded-3xl p-10 lg:p-12 relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:scale-110 transition-transform">
                <Truck className="w-48 h-48 text-indigo-600" />
              </div>
              <div className="relative z-10">
                <span className="px-3 py-1 bg-indigo-600 text-white rounded-full text-xs font-bold uppercase tracking-wider mb-6 inline-block">India Everywhere</span>
                <h3 className="text-4xl font-black text-indigo-950 mb-4">Domestic Shipping</h3>
                <p className="text-indigo-800/80 text-lg mb-8 max-w-md">Reach 29,000+ pincodes across the country via multiple tier-1 couriers (Delhivery, Xpressbees, Shadowfax). Ensure Cash-on-Delivery collections are secure with an impressive 24-hr payout cycle.</p>
                <button
                  onClick={() => navigate('/signup')}
                  className="font-bold border-b-2 border-indigo-600 text-indigo-600 pb-1 flex items-center gap-2 hover:gap-3 transition-all"
                >
                  Ship within India <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* International Block */}
            <div className="bg-slate-900 border border-slate-800 rounded-3xl p-10 lg:p-12 relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:scale-110 transition-transform">
                <Globe2 className="w-48 h-48 text-white" />
              </div>
              <div className="relative z-10">
                <span className="px-3 py-1 bg-emerald-500 text-white rounded-full text-xs font-bold uppercase tracking-wider mb-6 inline-block">Global Coverage</span>
                <h3 className="text-4xl font-black text-white mb-4">International Shipping</h3>
                <p className="text-slate-400 text-lg mb-8 max-w-md">Easily navigate customs constraints via our direct SKart integrations. Automatically sync multi-currency tracking, HS codes, and precise volumetric conversions serving 220+ countries.</p>
                <button
                  onClick={() => navigate('/signup')}
                  className="font-bold border-b-2 border-emerald-400 text-emerald-400 pb-1 flex items-center gap-2 hover:gap-3 transition-all"
                >
                  Go Global Today <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Features */}
      <section id="features" className="py-24 bg-slate-900 text-white relative overflow-hidden scroll-mt-32">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="mb-16 md:flex justify-between items-end">
            <div className="max-w-2xl">
              <h2 className="text-3xl md:text-5xl font-black mb-6 tracking-tight">Smart Value Add-Ons for <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">Five-Star Experiences</span></h2>
              <p className="text-slate-400 text-lg">Leave no room for error with intelligent routing, proactive customer reachouts, and rock-solid remittance cycles.</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feat, idx) => {
              const Icon = feat.icon;
              return (
                <div key={idx} className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-6 hover:bg-slate-800 transition-colors">
                  <div className="w-12 h-12 bg-indigo-500/20 rounded-lg flex items-center justify-center mb-6">
                    <Icon className="w-6 h-6 text-indigo-400" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{feat.title}</h3>
                  <p className="text-slate-400 leading-relaxed">{feat.desc}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Stats & Testimonials */}
      <section className="py-24 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-5xl font-black text-slate-900 mb-6 tracking-tight">Watch logistics turn into a growth advantage.</h2>
              <p className="text-lg text-slate-600 mb-10">Thousands of sellers have accelerated their growth by focusing entirely on their product while we completely handle the logistics backend.</p>

              <div className="grid grid-cols-2 gap-8">
                <div>
                  <div className="text-5xl font-black text-indigo-600 mb-2">1 Lakh+</div>
                  <div className="text-slate-500 font-bold tracking-wide uppercase text-sm">Happy Clients</div>
                </div>
                <div>
                  <div className="text-5xl font-black text-emerald-500 mb-2">29k+</div>
                  <div className="text-slate-500 font-bold tracking-wide uppercase text-sm">Pincodes Covered</div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              {testimonials.map((test, idx) => (
                <div key={idx} className="bg-slate-50 p-6 rounded-2xl border border-slate-100 flex gap-4 hover:shadow-md transition-shadow">
                  <div className="shrink-0 flex flex-col items-center justify-center w-16 h-16 bg-white rounded-xl shadow-sm border border-slate-200">
                    <Star className="w-6 h-6 text-yellow-400 fill-yellow-400 mb-1" />
                    <span className="text-xs font-black text-slate-700">5.0</span>
                  </div>
                  <div>
                    <h4 className="font-black text-slate-900 text-lg mb-1">{test.metric}</h4>
                    <p className="text-slate-600 italic mb-3 text-sm">"{test.quote}"</p>
                    <div className="text-sm font-bold text-slate-900">{test.name} <span className="font-medium text-slate-400">· {test.company}</span></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-24 bg-indigo-600 text-center px-4">
        <h2 className="text-4xl md:text-6xl font-black text-white mb-6 tracking-tight">Stop stressing over logistics.</h2>
        <p className="text-xl text-indigo-100 mb-10 max-w-2xl mx-auto">Join the fastest growing network of sellers getting the best rates, faster cod cycles, and absolute peace of mind.</p>
        <button
          onClick={() => navigate('/signup')}
          className="px-10 py-5 bg-white text-indigo-600 rounded-xl hover:bg-slate-50 hover:scale-105 transition-all font-black text-xl shadow-2xl"
        >
          Sign Up For Free
        </button>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
